import {
  ExpertNoteIdentifier,
  type MissionAssessmentFullSnapshotDto,
  MissionAssessmentInterType,
} from '@@types/api.types';
import {
  FULL_ASSESSMENT_SECTION_LABELS,
  FULL_ASSESSMENT_SECTION_ORDER,
  FULL_ASSESSMENT_SECTION_WAVES,
  FullAssessmentSectionId,
  type FullAssessmentSectionResult,
  FullAssessmentSectionStatus,
} from '@azymmetric/shared/types/full-assessment.types';
import type { LlmUIMessage } from '@azymmetric/shared/types/llm.types';
import {
  MA_FULL_SNAPSHOT_INTER_NAME_MAX_LENGTH,
  MA_FULL_SNAPSHOT_SERVICE_UNIT_MAX_LENGTH,
  MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH,
} from '@azymmetric/shared/validation/validation.constants';
import type { MissionAssessmentFormData } from '@schemas/mission-assessment.schema';
import {
  missionAssessmentEffectCreateSchema,
  missionAssessmentHurdlesSchema,
  missionAssessmentInterCreateSchema,
  missionAssessmentServiceCreateSchema,
  missionAssessmentTechnologyUseCreateSchema,
} from '@schemas/mission-assessment.schema';
import type { ChatStatus } from 'ai';
import { z } from 'zod/v4';

export { FULL_ASSESSMENT_SECTION_LABELS };

export const GENERATE_FULL_ASSESSMENT_MESSAGE =
  'Generate a complete draft for every section of this Mission Assessment.';

export type FullAssessmentSectionStates = {
  results: Map<FullAssessmentSectionId, FullAssessmentSectionResult>;
  /**
   * Sections currently being regenerated by an in-flight regenerateSection tool
   * call (orchestrator path) — their last completed draft stays visible while
   * the card shows progress.
   */
  streamingSections: Set<FullAssessmentSectionId>;
};

const SECTION_ID_VALUES = new Set<string>(Object.values(FullAssessmentSectionId));

const isSectionId = (value: unknown): value is FullAssessmentSectionId =>
  typeof value === 'string' && SECTION_ID_VALUES.has(value);

export const hasHurdlesContent = (hurdles: MissionAssessmentFormData['data']['hurdles']): boolean => {
  return Boolean(
    hurdles &&
      (hurdles.nextMrl?.trim() ||
        hurdles.nextMrlScore !== undefined ||
        hurdles.nextTrl?.trim() ||
        hurdles.nextTrlScore !== undefined ||
        hurdles.supplyChain?.trim()),
  );
};

const shouldKeepExistingCompletedResult = (params: {
  current?: FullAssessmentSectionResult;
  next: FullAssessmentSectionResult;
}): boolean => {
  const { current, next } = params;
  return (
    current?.status === FullAssessmentSectionStatus.COMPLETED && next.status === FullAssessmentSectionStatus.FAILED
  );
};

/**
 * A queued/streaming result can only be live while its exchange is still
 * streaming, and only the latest message can be streaming. Anything else is a
 * leftover from an interrupted exchange (network drop, server restart, closed
 * stream): restore the section's last completed draft when one exists,
 * otherwise mark it failed — never leave a section waiting forever.
 */
const reconcileInterruptedResults = (params: {
  chatStatus?: ChatStatus;
  lastCompletedResults: Map<FullAssessmentSectionId, FullAssessmentSectionResult>;
  lastMessageIndex: number;
  resultMessageIndexes: Map<FullAssessmentSectionId, number>;
  results: Map<FullAssessmentSectionId, FullAssessmentSectionResult>;
  streamingSections: Set<FullAssessmentSectionId>;
}): void => {
  const { chatStatus, lastCompletedResults, lastMessageIndex, resultMessageIndexes, results, streamingSections } =
    params;
  const isStreamOpen = chatStatus === 'submitted' || chatStatus === 'streaming';

  for (const [sectionId, result] of results) {
    const isPending =
      result.status === FullAssessmentSectionStatus.QUEUED || result.status === FullAssessmentSectionStatus.STREAMING;
    if (!isPending) continue;
    if (isStreamOpen && resultMessageIndexes.get(sectionId) === lastMessageIndex) continue;

    streamingSections.delete(sectionId);
    const lastCompleted = lastCompletedResults.get(sectionId);
    results.set(
      sectionId,
      lastCompleted ?? {
        errorMessage: 'Generation was interrupted before this section finished. Retry it to generate a new draft.',
        sectionId,
        status: FullAssessmentSectionStatus.FAILED,
        wave: result.wave,
      },
    );
  }
};

/**
 * Latest result per section across the whole conversation: fan-out
 * `data-section-result` parts (reconciled in place by id) and orchestrator
 * `regenerateSection` tool outputs both carry the same result contract.
 */
export const deriveSectionStates = (messages: LlmUIMessage[], chatStatus?: ChatStatus): FullAssessmentSectionStates => {
  const results = new Map<FullAssessmentSectionId, FullAssessmentSectionResult>();
  const streamingSections = new Set<FullAssessmentSectionId>();
  const lastCompletedResults = new Map<FullAssessmentSectionId, FullAssessmentSectionResult>();
  const resultMessageIndexes = new Map<FullAssessmentSectionId, number>();
  const setResult = (
    result: FullAssessmentSectionResult,
    messageIndex: number,
    options?: { forceOverwrite?: boolean },
  ) => {
    const current = results.get(result.sectionId);
    if (!options?.forceOverwrite && shouldKeepExistingCompletedResult({ current, next: result })) return;
    results.set(result.sectionId, result);
    resultMessageIndexes.set(result.sectionId, messageIndex);
    if (result.status === FullAssessmentSectionStatus.COMPLETED) {
      lastCompletedResults.set(result.sectionId, result);
    }
  };

  for (const [messageIndex, message] of messages.entries()) {
    for (const part of message.parts) {
      if (part.type === 'data-section-result') {
        setResult(part.data, messageIndex);
        if (part.data.status === FullAssessmentSectionStatus.STREAMING) {
          streamingSections.add(part.data.sectionId);
        } else {
          streamingSections.delete(part.data.sectionId);
        }
        continue;
      }

      if (part.type === 'data-section-text-delta') {
        const current = results.get(part.data.sectionId);
        const currentText = current?.status === FullAssessmentSectionStatus.STREAMING ? (current.text ?? '') : '';
        setResult(
          {
            logId: part.data.logId,
            sectionId: part.data.sectionId,
            status: FullAssessmentSectionStatus.STREAMING,
            text: `${currentText}${part.data.delta}`,
            wave: part.data.wave,
          },
          messageIndex,
        );
        streamingSections.add(part.data.sectionId);
        continue;
      }

      if (part.type !== 'tool-regenerateSection') continue;

      if (part.state === 'output-available') {
        setResult(part.output, messageIndex);
        streamingSections.delete(part.output.sectionId);
        continue;
      }

      if (part.state === 'output-error') {
        const sectionId = part.input?.sectionId;
        if (isSectionId(sectionId)) {
          streamingSections.delete(sectionId);
          // An explicit regeneration that failed must surface as FAILED even
          // when a prior completed draft exists — otherwise the card silently
          // reverts to the old draft and the failure is invisible. (Interrupted
          // regenerations, which never emit output-error, still keep the old
          // draft via reconcileInterruptedResults.)
          setResult(
            {
              errorMessage: part.errorText,
              sectionId,
              status: FullAssessmentSectionStatus.FAILED,
              wave: FULL_ASSESSMENT_SECTION_WAVES[sectionId],
            },
            messageIndex,
            { forceOverwrite: true },
          );
        }
        continue;
      }

      if (part.state === 'input-available' || part.state === 'input-streaming') {
        const sectionId = part.input?.sectionId;
        if (isSectionId(sectionId)) {
          streamingSections.add(sectionId);
        }
      }
    }
  }

  if (chatStatus !== 'submitted' && chatStatus !== 'streaming') {
    streamingSections.clear();
  }

  reconcileInterruptedResults({
    chatStatus,
    lastCompletedResults,
    lastMessageIndex: messages.length - 1,
    resultMessageIndexes,
    results,
    streamingSections,
  });

  return { results, streamingSections };
};

export const isFullAssessmentGenerationBusy = (params: {
  isRequestInFlight?: boolean;
  sectionStates: FullAssessmentSectionStates;
  status?: ChatStatus;
}): boolean => {
  const { isRequestInFlight, sectionStates, status } = params;

  if (isRequestInFlight) return true;
  if (status === 'submitted' || status === 'streaming') return true;
  if (status === 'error') return false;
  if (sectionStates.streamingSections.size > 0) return true;

  for (const result of sectionStates.results.values()) {
    if (
      result.status === FullAssessmentSectionStatus.QUEUED ||
      result.status === FullAssessmentSectionStatus.STREAMING
    ) {
      return true;
    }
  }

  return false;
};

// =============================================================================
// FORM SNAPSHOT (current form values ride every exchange)
// See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D6)
// =============================================================================

// The editable Mission Assessment fields are uncapped, so an accepted long draft
// (or pasted note) can exceed the snapshot caps enforced by
// MissionAssessmentFullSnapshotDto on the API. The snapshot only rides along as
// LLM context, so we clamp over-long entries to the shared caps here instead of
// letting the generate request 422.
const clampSnapshotText = (value: string, max: number): string => (value.length > max ? value.slice(0, max) : value);

export const buildFormSnapshot = (values: MissionAssessmentFormData): MissionAssessmentFullSnapshotDto => {
  const hurdles = values.data.hurdles;
  const hasHurdles = hasHurdlesContent(hurdles);
  const clampedHurdles = hurdles && {
    ...hurdles,
    ...(hurdles.nextMrl && { nextMrl: clampSnapshotText(hurdles.nextMrl, MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH) }),
    ...(hurdles.nextTrl && { nextTrl: clampSnapshotText(hurdles.nextTrl, MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH) }),
    ...(hurdles.supplyChain && {
      supplyChain: clampSnapshotText(hurdles.supplyChain, MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH),
    }),
  };

  const expertNotes = (values.data.expertNotes ?? [])
    .filter((note) => note.content.trim())
    .map((note) => ({ ...note, content: clampSnapshotText(note.content, MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH) }));
  const effects = values.effects
    .filter((effect) => effect.effectId || effect.useId || effect.domainId)
    .map((effect) => ({
      ...(effect.domainId && { domainId: effect.domainId }),
      ...(effect.effectId && { effectId: effect.effectId }),
      ...(effect.useId && { useId: effect.useId }),
    }));
  const inters = values.inters
    .filter((inter) => inter.name?.trim())
    .map((inter) => ({
      name: inter.name ? clampSnapshotText(inter.name, MA_FULL_SNAPSHOT_INTER_NAME_MAX_LENGTH) : inter.name,
      type: inter.type,
    }));
  const recommendedServices = values.recommendedServices
    .filter((service) => service.serviceId)
    .map((service) => ({
      serviceId: service.serviceId,
      ...(service.unit && { unit: clampSnapshotText(service.unit, MA_FULL_SNAPSHOT_SERVICE_UNIT_MAX_LENGTH) }),
    }));
  const recommendedTechnologyUses = values.recommendedTechnologyUses
    .filter((techUse) => techUse.useId)
    .map((techUse) => ({
      domainIds: techUse.domains.map((domain) => domain.domainId).filter(Boolean),
      useId: techUse.useId,
    }));
  const taskRecommendations = values.taskRecommendations
    .map((task) => task.description?.trim() ?? '')
    .filter(Boolean)
    .map((description) => clampSnapshotText(description, MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH));

  return {
    ...(values.capabilities.length > 0 && { capabilities: values.capabilities }),
    ...(values.communities.length > 0 && { communities: values.communities }),
    ...(effects.length > 0 && { effects }),
    ...(expertNotes.length > 0 && { expertNotes }),
    ...(values.data.generalNotes?.trim() && {
      generalNotes: clampSnapshotText(values.data.generalNotes, MA_FULL_SNAPSHOT_TEXT_MAX_LENGTH),
    }),
    ...(hasHurdles && clampedHurdles && { hurdles: clampedHurdles }),
    ...(inters.length > 0 && { inters }),
    ...(values.killChains.length > 0 && { killChains: values.killChains }),
    ...(values.data.maxPhase !== undefined && { maxPhase: values.data.maxPhase }),
    ...(values.data.minPhase !== undefined && { minPhase: values.data.minPhase }),
    ...(recommendedServices.length > 0 && { recommendedServices }),
    ...(recommendedTechnologyUses.length > 0 && { recommendedTechnologyUses }),
    ...(taskRecommendations.length > 0 && { taskRecommendations }),
  };
};

// =============================================================================
// SECTION PATCHES (validated draft payload → typed form patch)
// =============================================================================

// z.guid(), not z.uuid(): catalog ids are server-issued and some seeded rows
// (e.g. Kill Chain "Engage"/"Assess") are uuid-shaped but not RFC-4122
// version-valid — z.uuid() rejects them and the draft becomes undisplayable.
const uuidArraySchema = z.array(z.guid()).min(1);

const capabilitiesPayloadSchema = z.object({ capabilities: uuidArraySchema });
const communitiesPayloadSchema = z.object({ communities: uuidArraySchema });
const killChainsPayloadSchema = z.object({ killChains: uuidArraySchema });
const servicesPayloadSchema = z.object({
  recommendedServices: z.array(missionAssessmentServiceCreateSchema).min(1),
});
const technologyUsesPayloadSchema = z.object({
  recommendedTechnologyUses: z.array(missionAssessmentTechnologyUseCreateSchema).min(1),
});
const tasksPayloadSchema = z.object({
  taskRecommendations: z.array(z.object({ description: z.string().min(1) })).min(1),
});
const effectsPayloadSchema = z.object({
  effects: z.array(missionAssessmentEffectCreateSchema).min(1),
});
const intersPayloadSchema = z.object({
  inters: z.array(missionAssessmentInterCreateSchema).min(1),
});
const phasesPayloadSchema = z.object({
  maxPhase: z.number().int().min(0).max(5),
  minPhase: z.number().int().min(0).max(5),
});
const hurdlesPayloadSchema = z.object({ hurdles: missionAssessmentHurdlesSchema });

const EXPERT_NOTE_SECTION_IDENTIFIERS: Partial<Record<FullAssessmentSectionId, ExpertNoteIdentifier>> = {
  [FullAssessmentSectionId.RISKS]: ExpertNoteIdentifier.RISKS,
  [FullAssessmentSectionId.STRATEGIC]: ExpertNoteIdentifier.STRATEGIC,
  [FullAssessmentSectionId.SUSTAINMENT]: ExpertNoteIdentifier.SUSTAINMENT,
  [FullAssessmentSectionId.THREATS]: ExpertNoteIdentifier.THREATS,
};

// Patch values are the form schemas' OUTPUT types so accepts write exactly what
// the form fields hold (the same schemas parse the streamed payloads).
type EffectCreateValue = z.output<typeof missionAssessmentEffectCreateSchema>;
type HurdlesValue = z.output<typeof missionAssessmentHurdlesSchema>;
type InterCreateValue = z.output<typeof missionAssessmentInterCreateSchema>;
type ServiceCreateValue = z.output<typeof missionAssessmentServiceCreateSchema>;
type TechnologyUseCreateValue = z.output<typeof missionAssessmentTechnologyUseCreateSchema>;

export type FullAssessmentSectionPatch =
  | { kind: 'capabilities'; value: string[] }
  | { kind: 'communities'; value: string[] }
  | { kind: 'effects'; value: EffectCreateValue[] }
  | { kind: 'expertNote'; identifier: ExpertNoteIdentifier; value: string }
  | { kind: 'generalNotes'; value: string }
  | { kind: 'hurdles'; value: HurdlesValue }
  | { kind: 'inters'; type: MissionAssessmentInterType; value: InterCreateValue[] }
  | { kind: 'killChains'; value: string[] }
  | { kind: 'phases'; value: { maxPhase: number; minPhase: number } }
  | { kind: 'services'; value: ServiceCreateValue[] }
  | { kind: 'tasks'; value: Array<{ description: string }> }
  | { kind: 'techUses'; value: TechnologyUseCreateValue[] };

/**
 * Validates a completed section result into a typed form patch. Returns null
 * when the payload does not parse — the card then refuses the accept instead
 * of writing malformed data into the form.
 */
export const parseSectionPatch = (result: FullAssessmentSectionResult): FullAssessmentSectionPatch | null => {
  const expertNoteIdentifier = EXPERT_NOTE_SECTION_IDENTIFIERS[result.sectionId];
  if (expertNoteIdentifier) {
    const content = result.text?.trim();
    return content ? { identifier: expertNoteIdentifier, kind: 'expertNote', value: content } : null;
  }

  if (result.sectionId === FullAssessmentSectionId.GENERAL_NOTES) {
    const content = result.text?.trim();
    return content ? { kind: 'generalNotes', value: content } : null;
  }

  if (!result.data) return null;

  switch (result.sectionId) {
    case FullAssessmentSectionId.CAPABILITIES: {
      const parsed = capabilitiesPayloadSchema.safeParse(result.data);
      return parsed.success ? { kind: 'capabilities', value: parsed.data.capabilities } : null;
    }
    case FullAssessmentSectionId.COMMUNITIES: {
      const parsed = communitiesPayloadSchema.safeParse(result.data);
      return parsed.success ? { kind: 'communities', value: parsed.data.communities } : null;
    }
    case FullAssessmentSectionId.KILL_CHAINS: {
      const parsed = killChainsPayloadSchema.safeParse(result.data);
      return parsed.success ? { kind: 'killChains', value: parsed.data.killChains } : null;
    }
    case FullAssessmentSectionId.RECOMMENDED_SERVICES: {
      const parsed = servicesPayloadSchema.safeParse(result.data);
      return parsed.success ? { kind: 'services', value: parsed.data.recommendedServices } : null;
    }
    case FullAssessmentSectionId.RECOMMENDED_TECHNOLOGY_USES: {
      const parsed = technologyUsesPayloadSchema.safeParse(result.data);
      return parsed.success ? { kind: 'techUses', value: parsed.data.recommendedTechnologyUses } : null;
    }
    case FullAssessmentSectionId.TASK_RECOMMENDATIONS: {
      const parsed = tasksPayloadSchema.safeParse(result.data);
      return parsed.success ? { kind: 'tasks', value: parsed.data.taskRecommendations } : null;
    }
    case FullAssessmentSectionId.EFFECTS: {
      const parsed = effectsPayloadSchema.safeParse(result.data);
      return parsed.success ? { kind: 'effects', value: parsed.data.effects } : null;
    }
    case FullAssessmentSectionId.INTEROPERABLE:
    case FullAssessmentSectionId.INTERCHANGEABLE: {
      const parsed = intersPayloadSchema.safeParse(result.data);
      if (!parsed.success) return null;

      const type =
        result.sectionId === FullAssessmentSectionId.INTEROPERABLE
          ? MissionAssessmentInterType.INTEROPERABLE
          : MissionAssessmentInterType.INTERCHANGEABLE;

      return { kind: 'inters', type, value: parsed.data.inters.map((inter) => ({ ...inter, type })) };
    }
    case FullAssessmentSectionId.PHASES: {
      const parsed = phasesPayloadSchema.safeParse(result.data);
      return parsed.success ? { kind: 'phases', value: parsed.data } : null;
    }
    case FullAssessmentSectionId.HURDLES: {
      const parsed = hurdlesPayloadSchema.safeParse(result.data);
      return parsed.success ? { kind: 'hurdles', value: parsed.data.hurdles } : null;
    }
    default:
      return null;
  }
};

/**
 * A section draft is acceptable when it completed with a payload that parses
 * into a form patch (Accept All applies exactly these — D7).
 */
export const isSectionAcceptable = (result: FullAssessmentSectionResult | undefined): boolean => {
  if (!result || result.status !== FullAssessmentSectionStatus.COMPLETED) return false;
  return parseSectionPatch(result) !== null;
};

export const isUnresolvedDraft = (params: {
  acceptedLogIds: Set<string>;
  isAcceptable?: boolean;
  rejectedLogIds: Set<string>;
  result?: FullAssessmentSectionResult;
}): boolean => {
  const { acceptedLogIds, isAcceptable, rejectedLogIds, result } = params;
  if (!(isAcceptable ?? isSectionAcceptable(result))) return false;
  return !result?.logId || (!acceptedLogIds.has(result.logId) && !rejectedLogIds.has(result.logId));
};

const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`)
      .join(',')}}`;
  }

  return JSON.stringify(value) ?? 'undefined';
};

/**
 * Key-order-insensitive structural comparison: when a draft matches the form's
 * current content (e.g. right after an Accept), there is nothing to compare and
 * the Proposed | Current toggle should not render.
 */
export const sectionPatchesEqual = (left: FullAssessmentSectionPatch, right: FullAssessmentSectionPatch): boolean =>
  stableStringify(left) === stableStringify(right);

// =============================================================================
// SEEDS (current form content shown per section until a draft supersedes)
// See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D6)
// =============================================================================

/**
 * Maps the current form values into the same patch shapes the generated drafts
 * use, so "current content" renders through exactly the same UI as proposals.
 * Sections without content have no entry.
 */
export const buildSeedPatches = (
  values: MissionAssessmentFormData,
): Map<FullAssessmentSectionId, FullAssessmentSectionPatch> => {
  const patches = new Map<FullAssessmentSectionId, FullAssessmentSectionPatch>();

  if (values.capabilities.length > 0) {
    patches.set(FullAssessmentSectionId.CAPABILITIES, { kind: 'capabilities', value: values.capabilities });
  }
  if (values.communities.length > 0) {
    patches.set(FullAssessmentSectionId.COMMUNITIES, { kind: 'communities', value: values.communities });
  }
  if (values.killChains.length > 0) {
    patches.set(FullAssessmentSectionId.KILL_CHAINS, { kind: 'killChains', value: values.killChains });
  }

  const services = values.recommendedServices.filter((service) => service.serviceId);
  if (services.length > 0) {
    patches.set(FullAssessmentSectionId.RECOMMENDED_SERVICES, { kind: 'services', value: services });
  }

  const techUses = values.recommendedTechnologyUses.filter((techUse) => techUse.useId);
  if (techUses.length > 0) {
    patches.set(FullAssessmentSectionId.RECOMMENDED_TECHNOLOGY_USES, { kind: 'techUses', value: techUses });
  }

  const tasks = values.taskRecommendations.filter((task) => task.description?.trim());
  if (tasks.length > 0) {
    patches.set(FullAssessmentSectionId.TASK_RECOMMENDATIONS, { kind: 'tasks', value: tasks });
  }

  const effects = values.effects.filter((effect) => effect.effectId);
  if (effects.length > 0) {
    patches.set(FullAssessmentSectionId.EFFECTS, { kind: 'effects', value: effects });
  }

  const interSeed = (
    sectionId: FullAssessmentSectionId.INTERCHANGEABLE | FullAssessmentSectionId.INTEROPERABLE,
    type: MissionAssessmentInterType,
  ) => {
    const inters = values.inters.filter((inter) => inter.type === type && inter.name?.trim());
    if (inters.length > 0) {
      patches.set(sectionId, { kind: 'inters', type, value: inters });
    }
  };
  interSeed(FullAssessmentSectionId.INTEROPERABLE, MissionAssessmentInterType.INTEROPERABLE);
  interSeed(FullAssessmentSectionId.INTERCHANGEABLE, MissionAssessmentInterType.INTERCHANGEABLE);

  if (values.data.minPhase !== undefined && values.data.maxPhase !== undefined) {
    patches.set(FullAssessmentSectionId.PHASES, {
      kind: 'phases',
      value: { maxPhase: values.data.maxPhase, minPhase: values.data.minPhase },
    });
  }

  const hurdles = values.data.hurdles;
  if (hurdles && hasHurdlesContent(hurdles)) {
    patches.set(FullAssessmentSectionId.HURDLES, {
      kind: 'hurdles',
      value: {
        nextMrl: hurdles.nextMrl,
        nextMrlScore: hurdles.nextMrlScore,
        nextTrl: hurdles.nextTrl,
        nextTrlScore: hurdles.nextTrlScore,
        supplyChain: hurdles.supplyChain,
      },
    });
  }

  const expertNoteSeed = (sectionId: FullAssessmentSectionId, identifier: ExpertNoteIdentifier) => {
    const note = values.data.expertNotes?.find((entry) => entry.identifier === identifier);
    if (note?.content.trim()) {
      patches.set(sectionId, { identifier, kind: 'expertNote', value: note.content.trim() });
    }
  };
  expertNoteSeed(FullAssessmentSectionId.THREATS, ExpertNoteIdentifier.THREATS);
  expertNoteSeed(FullAssessmentSectionId.STRATEGIC, ExpertNoteIdentifier.STRATEGIC);
  expertNoteSeed(FullAssessmentSectionId.RISKS, ExpertNoteIdentifier.RISKS);
  expertNoteSeed(FullAssessmentSectionId.SUSTAINMENT, ExpertNoteIdentifier.SUSTAINMENT);

  if (values.data.generalNotes?.trim()) {
    patches.set(FullAssessmentSectionId.GENERAL_NOTES, {
      kind: 'generalNotes',
      value: values.data.generalNotes.trim(),
    });
  }

  return patches;
};

export const orderedSectionIds = (): FullAssessmentSectionId[] => [...FULL_ASSESSMENT_SECTION_ORDER];
